/**
 * What happens to documents AFTER the vector store returns them: re-ranking for
 * WTC relevance, and rendering them into prompt context.
 *
 * Split out of `../query-enhancer.ts`. This is the other half of retrieval — the
 * query side decides what can be found, this side decides what survives — and it
 * changes for different reasons, so it changes in a different file.
 *
 * The boosts are additive and hand-tuned; the point of the cap is that a
 * document cannot out-score a perfect vector match on keyword presence alone.
 */

/** A retrieved document, before ranking. Shape is deliberately loose: providers differ. */
export interface RankableDocument {
  content: string;
  score?: number;
  metadata?: Record<string, unknown>;
  id?: string;
  source?: string;
}

export type RankedDocument = RankableDocument & { relevanceScore: number };

/**
 * Filter and rank retrieved documents based on relevance to WTC operations
 *
 * @param documents - Retrieved documents from knowledge base
 * @param query - The original query
 * @returns Filtered and ranked documents
 */
export function rankDocumentsByRelevance(
  documents: RankableDocument[],
  query: string
): RankedDocument[] {
  void query; // Ranking is content-driven today; the query is kept for future tuning.

  return documents
    .map((doc) => {
      let relevanceScore = doc.score || 0;
      const lowerContent = doc.content.toLowerCase();

      // Boost score for WTC and Hub operations focus
      if (lowerContent.includes('wtc') || lowerContent.includes('west tennessee')) relevanceScore += 0.2;
      if (lowerContent.includes('sales') || lowerContent.includes('pipeline') || lowerContent.includes('deal'))
        relevanceScore += 0.15;
      if (lowerContent.includes('operations') || lowerContent.includes('ticket') || lowerContent.includes('msp'))
        relevanceScore += 0.15;
      if (lowerContent.includes('actionable') || lowerContent.includes('best practice')) relevanceScore += 0.1;
      if (lowerContent.includes('contact') || lowerContent.includes('crm') || lowerContent.includes('hub'))
        relevanceScore += 0.1;

      // Boost for actionable content
      if (lowerContent.includes('step') || lowerContent.includes('how to') || lowerContent.includes('guide')) {
        relevanceScore += 0.15;
      }

      // Penalize if it's off-topic (e.g., personal credit, unrelated domains)
      if (
        lowerContent.includes('personal credit') &&
        !lowerContent.includes('business') &&
        !lowerContent.includes('company')
      ) {
        relevanceScore -= 0.3;
      }

      return {
        ...doc,
        relevanceScore: Math.min(1, relevanceScore), // Cap at 1.0
      };
    })
    .sort((a, b) => b.relevanceScore - a.relevanceScore);
}

/**
 * Format retrieved documents with Meridian's WTC operations perspective
 *
 * @param documents - Retrieved documents
 * @param locale - The language locale (default: 'en')
 * @returns Formatted context string
 */
export function formatDocumentsWithMeridianPerspective(
  documents: Array<{ content: string; source?: string; metadata?: Record<string, unknown> }>,
  locale: string = 'en'
): string {
  if (documents.length === 0) {
    return '';
  }

  const header =
    locale === 'es'
      ? 'Basado en el conocimiento de operaciones WTC y mejores prácticas:'
      : 'Based on WTC operations knowledge and best practices:';

  const formattedDocs = documents
    .map((doc, index) => {
      const source = doc.source || 'WTC Knowledge Base';
      return `[${index + 1}] ${source}:\n${doc.content}`;
    })
    .join('\n\n');

  return `${header}\n\n${formattedDocs}`;
}
